import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { playersAPI } from '../services/api';
import { format, formatDistanceToNow } from 'date-fns';
import { vi } from 'date-fns/locale';
import {
    FiSearch, FiRefreshCw, FiUsers, FiUser, FiAward, FiWifi,
} from 'react-icons/fi';
import '../styles/PlayerList.css';

const fmt = (n) => (n != null ? n.toLocaleString('vi-VN') : '0');

const TABS = [
    { key: 'all',     label: 'Tất Cả',         icon: <FiUsers />, sortBy: 'total'    },
    { key: 'rich',    label: 'Top Giàu Nhất',  icon: <FiAward />, sortBy: 'total'    },
    { key: 'online',  label: 'Đang Online',    icon: <FiWifi />,  sortBy: 'lastSeen' },
];

const PlayerRow = ({ p, rank, showRank }) => {
    const total = (p.cash || 0) + (p.bank || 0) + (p.crypto || 0);
    const rankIcon = rank === 1 ? '🥇' : rank === 2 ? '🥈' : rank === 3 ? '🥉' : `#${rank}`;
    return (
        <tr className="pl-row">
            {showRank && <td className="pl-rank">{rankIcon}</td>}
            <td>
                <Link to={`/fivem-player/${encodeURIComponent(p.citizenId)}`}
                      className={`pl-name-btn ${p.isAdmin ? 'pl-name-btn-admin' : ''}`}>
                    <FiUser className="pl-name-icon" />
                    <span className="pl-name">{p.name || p.citizenId}</span>
                    {p.isAdmin && (
                        <span className={`pl-admin-tag pl-admin-${p.adminLevel || 'admin'}`}
                              title={`Quyền ${(p.adminLevel || 'admin').toUpperCase()}`}>
                            {(p.adminLevel || 'admin').toUpperCase() === 'GOD' ? '👑' : '🛡️'} {(p.adminLevel || 'admin').toUpperCase()}
                        </span>
                    )}
                    {p._isLite && <span className="pl-lite-tag">lite</span>}
                </Link>
            </td>
            <td className="pl-cid"><code>{p.citizenId}</code></td>
            <td className="pl-money pl-cash">${fmt(p.cash)}</td>
            <td className="pl-money pl-bank">${fmt(p.bank)}</td>
            <td className="pl-money pl-crypto">${fmt(p.crypto)}</td>
            <td className="pl-money pl-total">${fmt(total)}</td>
            <td className="pl-job">{p.jobLabel || p.jobName || '—'}</td>
            <td>
                <span className={`pl-status ${p.isOnline ? 'online' : 'offline'}`}>
                    {p.isOnline ? '🟢 Online' : '⚫ Offline'}
                </span>
            </td>
            <td className="pl-last">
                {p.lastSeenAt ? formatDistanceToNow(new Date(p.lastSeenAt), { addSuffix: true, locale: vi }) : '—'}
            </td>
        </tr>
    );
};

const PlayerList = () => {
    const [activeTab, setActiveTab] = useState('all');
    const [players, setPlayers]     = useState([]);
    const [stats, setStats]         = useState({ total: 0, onlineCount: 0, liteCount: 0, fullCount: 0 });
    const [loading, setLoading]     = useState(true);
    const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, pages: 0 });
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch]       = useState('');
    const [sortBy, setSortBy]       = useState('total');
    const [sortOrder, setSortOrder] = useState('desc');

    const fetchData = useCallback(async (page = 1) => {
        try {
            setLoading(true);
            if (activeTab === 'rich') {
                const res = await playersAPI.topRichest({ limit: 50 });
                setPlayers(res.data.players || []);
                setStats(prev => ({ ...prev, total: res.data.count }));
                setPagination({ page: 1, limit: 50, total: res.data.count, pages: 1 });
            } else {
                const params = {
                    page, limit: 50, search,
                    sortBy, sortOrder,
                    onlineOnly: activeTab === 'online' ? 'true' : undefined,
                };
                const res = await playersAPI.list(params);
                setPlayers(res.data.players || []);
                setStats(res.data.stats || stats);
                setPagination(res.data.pagination || pagination);
            }
        } catch (err) {
            console.error(err);
            toast.error('Không thể tải danh sách người chơi');
        } finally {
            setLoading(false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab, search, sortBy, sortOrder]);

    useEffect(() => { fetchData(1); }, [fetchData]);

    // Auto refresh mỗi 15s
    useEffect(() => {
        const id = setInterval(() => fetchData(pagination.page || 1), 15000);
        return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fetchData]);

    const handleSearch = (e) => {
        e.preventDefault();
        setSearch(searchInput);
    };

    const handleSort = (col) => {
        if (sortBy === col) setSortOrder(o => o === 'desc' ? 'asc' : 'desc');
        else { setSortBy(col); setSortOrder('desc'); }
    };

    const sortIcon = (col) => sortBy !== col ? '' : (sortOrder === 'desc' ? ' ▼' : ' ▲');

    const showRank = activeTab === 'rich';

    return (
        <div className="pl-page">
            <div className="pl-header">
                <div>
                    <h1 className="pl-title"><FiUsers /> Quản Lý Người Chơi</h1>
                    <p className="pl-subtitle">
                        {stats.total} người chơi
                        · {stats.onlineCount} đang online
                        · {stats.fullCount} FiveM sync · {stats.liteCount} lite
                        {stats.adminCount > 0 && <> · <span style={{color:'#fbbf24'}}>🛡️ {stats.adminCount} admin trang này</span></>}
                    </p>
                </div>
                <button className="pl-refresh-btn" onClick={() => fetchData(pagination.page)} disabled={loading}>
                    <FiRefreshCw className={loading ? 'spinning' : ''} />
                </button>
            </div>

            {/* Tabs */}
            <div className="pl-tabs">
                {TABS.map(t => (
                    <button
                        key={t.key}
                        className={`pl-tab ${activeTab === t.key ? 'active' : ''}`}
                        onClick={() => { setActiveTab(t.key); setSortBy(t.sortBy); }}
                    >
                        {t.icon} {t.label}
                        {t.key === 'rich' && <span className="pl-tab-badge">50</span>}
                        {t.key === 'online' && <span className="pl-tab-badge online">{stats.onlineCount}</span>}
                        {t.key === 'all' && <span className="pl-tab-badge">{stats.total}</span>}
                    </button>
                ))}
            </div>

            {/* Search */}
            {activeTab !== 'rich' && (
                <form className="pl-search-form" onSubmit={handleSearch}>
                    <div className="pl-search-wrap">
                        <FiSearch className="pl-search-icon" />
                        <input
                            className="pl-search-input"
                            placeholder="Tìm theo tên hoặc CitizenID..."
                            value={searchInput}
                            onChange={e => setSearchInput(e.target.value)}
                        />
                    </div>
                    <button type="submit" className="pl-search-btn">Tìm</button>
                    {search && (
                        <button type="button" className="pl-clear-btn" onClick={() => { setSearchInput(''); setSearch(''); }}>
                            Xóa
                        </button>
                    )}
                </form>
            )}

            {/* Table */}
            <div className="pl-table-wrap">
                {loading ? (
                    <div className="pl-state"><div className="loading-spinner" /><p>Đang tải...</p></div>
                ) : players.length === 0 ? (
                    <div className="pl-state"><p>Không có người chơi nào</p></div>
                ) : (
                    <table className="pl-table">
                        <thead>
                            <tr>
                                {showRank && <th style={{ width: 50 }}>#</th>}
                                <th onClick={() => handleSort('name')}>Tên Người Chơi{sortIcon('name')}</th>
                                <th>CitizenID</th>
                                <th onClick={() => handleSort('cash')}>Tiền Mặt{sortIcon('cash')}</th>
                                <th onClick={() => handleSort('bank')}>Ngân Hàng{sortIcon('bank')}</th>
                                <th>Crypto</th>
                                <th onClick={() => handleSort('total')}>Tổng{sortIcon('total')}</th>
                                <th>Công Việc</th>
                                <th>Trạng Thái</th>
                                <th onClick={() => handleSort('lastSeen')}>Hoạt Động{sortIcon('lastSeen')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {players.map((p, i) => (
                                <PlayerRow key={p.citizenId || i} p={p} rank={i + 1} showRank={showRank} />
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Pagination */}
            {pagination.pages > 1 && (
                <div className="pl-pager">
                    <button disabled={pagination.page <= 1} onClick={() => fetchData(pagination.page - 1)}>← Trước</button>
                    <span>Trang {pagination.page} / {pagination.pages} ({pagination.total.toLocaleString()} người)</span>
                    <button disabled={pagination.page >= pagination.pages} onClick={() => fetchData(pagination.page + 1)}>Sau →</button>
                </div>
            )}
        </div>
    );
};

export default PlayerList;
